/**
 * Routed overlay segments for the Customer Experience cloud pages (SPU-786). Paths carry only
 * opaque ids; names, emails, AX numbers, and ARNs never go into a URL. Each `*OverlayPaths`
 * helper appends its page's overlay patterns to a route base, for `allowedOverlayPath`.
 */

/** Overlays under `/cloud/subscription-management`, keyed by Partner Center customer id. */
export const CSP_SEGMENTS = {
  tenant: "tenant",
  newSubscription: "new-subscription",
  activityLog: "activity-log",
  attestation: "attestation",
  assignUsers: "assign-users",
  user: "user",
  product: "csp-product",
  assignLicenses: "assign-licenses",
  adjustSeats: "adjust-seats",
  subscription: "subscription",
  cancel: "cancel",
} as const;

export const cspOverlayPaths = (base: string): string[] => {
  const tenant = `${base}/${CSP_SEGMENTS.tenant}/:customerId`;
  const product = `${tenant}/${CSP_SEGMENTS.product}/:productId`;
  const subscription = `${tenant}/${CSP_SEGMENTS.subscription}/:subscriptionId`;
  return [
    `${tenant}/${CSP_SEGMENTS.newSubscription}`,
    `${tenant}/${CSP_SEGMENTS.activityLog}`,
    `${tenant}/${CSP_SEGMENTS.attestation}`,
    `${tenant}/${CSP_SEGMENTS.assignUsers}`,
    `${tenant}/${CSP_SEGMENTS.assignUsers}/${CSP_SEGMENTS.user}/:userId`,
    `${product}/${CSP_SEGMENTS.assignLicenses}`,
    `${product}/${CSP_SEGMENTS.assignLicenses}/${CSP_SEGMENTS.user}/:userId`,
    `${product}/${CSP_SEGMENTS.adjustSeats}`,
    subscription,
    `${subscription}/${CSP_SEGMENTS.cancel}`,
  ];
};

/** Cost center drawer and dialog segments, shared by `/cloud/cost-centers` and `/cloud/subscriptions`. */
export const COST_CENTER_SEGMENTS = {
  costCenter: "cost-center",
  newCostCenter: "new-cost-center",
} as const;

/** Cost center drawer tabs. The default tab is left out of the URL. */
export const COST_CENTER_DRAWER_TABS = {
  details: "details",
  cloudSubscriptions: "cloud-subscriptions",
} as const;

const costCenterDrawerPaths = (base: string): string[] => {
  const drawer = `${base}/${COST_CENTER_SEGMENTS.costCenter}/:costCenterId`;
  return [drawer, `${drawer}/${COST_CENTER_DRAWER_TABS.cloudSubscriptions}`];
};

export const costCentersOverlayPaths = (base: string): string[] => [
  ...costCenterDrawerPaths(base),
  `${base}/${COST_CENTER_SEGMENTS.newCostCenter}`,
];

/** Overlays under `/cloud/subscriptions`, keyed by configuration item id. */
export const CLOUD_SUBSCRIPTION_SEGMENTS = {
  subscription: "subscription",
  allocation: "allocation",
  chooseCostCenter: "choose-cost-center",
  awsGeneratedTags: "aws-generated-tags",
  userDefinedTags: "user-defined-tags",
  bulkAllocate: "bulk-allocate",
} as const;

export const cloudSubscriptionsOverlayPaths = (base: string): string[] => {
  const s = CLOUD_SUBSCRIPTION_SEGMENTS;
  const subscription = `${base}/${s.subscription}/:configurationItemId`;
  return [
    `${subscription}/${s.allocation}`,
    `${subscription}/${s.allocation}/${s.chooseCostCenter}`,
    `${subscription}/${s.awsGeneratedTags}`,
    `${subscription}/${s.userDefinedTags}`,
    ...costCenterDrawerPaths(base),
    `${base}/${s.bulkAllocate}`,
    `${base}/${s.bulkAllocate}/${s.chooseCostCenter}`,
  ];
};

export const AWS_ACCOUNTS_SEGMENTS = {
  addAccount: "add-account",
} as const;

export const awsAccountsOverlayPaths = (base: string): string[] => [
  `${base}/${AWS_ACCOUNTS_SEGMENTS.addAccount}`,
];

/** Overlays under `/cloud/aws/cat`, keyed by cloud account id. */
export const AWS_COST_ALLOCATION_TAG_SEGMENTS = {
  account: "account",
  awsGeneratedTags: "aws-generated-tags",
  userDefinedTags: "user-defined-tags",
} as const;

export const awsCostAllocationTagsOverlayPaths = (base: string): string[] => {
  const s = AWS_COST_ALLOCATION_TAG_SEGMENTS;
  const account = `${base}/${s.account}/:cloudAccountId`;
  return [`${account}/${s.awsGeneratedTags}`, `${account}/${s.userDefinedTags}`];
};

/**
 * Overlays under `/cloud/aws/recommendations`. Reservations and Trusted Advisor have no stable
 * id, so they route as transient fixed segments.
 */
export const AWS_RECOMMENDATION_SEGMENTS = {
  rightsizing: "rightsizing",
  savingsPlans: "savings-plans",
  reservations: "reservations",
  reservationDetails: "details",
  trustedAdvisor: "trusted-advisor",
  trustedAdvisorCheck: "check",
} as const;

export const awsRecommendationsOverlayPaths = (base: string): string[] => {
  const s = AWS_RECOMMENDATION_SEGMENTS;
  return [
    `${base}/${s.rightsizing}/:recommendationId`,
    `${base}/${s.savingsPlans}/:payerAccountId/:recommendationId`,
    `${base}/${s.reservations}/${s.reservationDetails}`,
    `${base}/${s.trustedAdvisor}/${s.trustedAdvisorCheck}`,
  ];
};

export const AZURE_SUBSCRIPTIONS_SEGMENTS = {
  addSubscription: "add-subscription",
} as const;

export const azureSubscriptionsOverlayPaths = (base: string): string[] => [
  `${base}/${AZURE_SUBSCRIPTIONS_SEGMENTS.addSubscription}`,
];

/** The security compliance recommendation has no stable id, so it routes as transient. */
export const AZURE_SECURITY_COMPLIANCE_SEGMENTS = {
  recommendation: "recommendation",
} as const;

export const azureSecurityComplianceOverlayPaths = (base: string): string[] => [
  `${base}/${AZURE_SECURITY_COMPLIANCE_SEGMENTS.recommendation}`,
];

export const AZURE_RECOMMENDATION_SEGMENTS = {
  recommendation: "recommendation",
} as const;

/** Advisor recommendation names repeat across subscriptions, so the subscription id is in the path. */
export const AZURE_RECOMMENDATION_OVERLAY_PATH = `${AZURE_RECOMMENDATION_SEGMENTS.recommendation}/:subscriptionId/:recommendationId`;

export const azureRecommendationsOverlayPaths = (base: string): string[] => [
  `${base}/${AZURE_RECOMMENDATION_OVERLAY_PATH}`,
];

/** Overlays under `/shi-internal/marketplaceAccounts`, keyed by marketplace tenant id. */
export const MARKETPLACE_ACCOUNT_SEGMENTS = {
  tenant: "tenant",
  assignAxNumber: "assign-ax-number",
} as const;

export const marketplaceAccountsOverlayPaths = (base: string): string[] => [
  `${base}/${MARKETPLACE_ACCOUNT_SEGMENTS.tenant}/:tenantId/${MARKETPLACE_ACCOUNT_SEGMENTS.assignAxNumber}`,
];
